import { useMemo, useState } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from './Toast';
import { theme } from '../theme';
import { Icon, categoryIconName } from './Icon';
import { fmtMoney, todayIso } from '../utils/format';
import { celebrateReward, rewardForOp } from '../utils/coins';
import { Category } from '../api/types';

/**
 * Быстрый ввод расхода прямо с обложки: сумма + чип частой повседневной
 * категории = операция сегодняшней датой в один тап.
 */
export function QuickAdd() {
  const { viewData, addTx } = useBudget();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [amountStr, setAmountStr] = useState('');
  const [busy, setBusy] = useState(false);

  // топ повседневных категорий по числу операций за последние ~60 дней
  const frequent = useMemo(() => {
    if (!viewData) return [];
    const d = new Date();
    d.setDate(d.getDate() - 60);
    const cutoff = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const count = new Map<string, number>();
    for (const op of viewData.operations) {
      if (op.date < cutoff) continue;
      count.set(op.category, (count.get(op.category) || 0) + 1);
    }
    const daily = viewData.categories.filter((c) => c.type === 'Расход' && c.is_daily);
    return daily
      .slice()
      .sort((a, b) => (count.get(b.name) || 0) - (count.get(a.name) || 0) || a.sort_order - b.sort_order)
      .slice(0, 5);
  }, [viewData]);

  const currency = viewData?.settings.currency ?? '₽';
  const amount = Number(amountStr.replace(',', '.'));
  const canAdd = Number.isFinite(amount) && amount > 0 && !busy;

  async function add(cat: Category) {
    if (!canAdd) {
      toast.show('введите сумму', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await addTx({ date: todayIso(), category: cat.name, amount });
      // награда детерминирована id — праздновать офлайн безопасно (запись доедет)
      celebrateReward(rewardForOp({ id: r.id, type: cat.type }), toast, {
        title: cat.name,
        sub: r.synced ? undefined : 'синхронизируется позже',
      });
      setAmountStr('');
      setOpen(false);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!viewData) return null;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          width: '100%',
          padding: '11px 0',
          background: 'transparent',
          border: `1px solid ${theme.colors.ink}`,
          borderRadius: theme.radius.sm,
          color: theme.colors.ink,
          fontFamily: theme.font,
          fontSize: 14,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <Icon name="plus" size={16} /> Быстрый расход
      </button>
    );
  }

  return (
    <div style={{ border: `1px solid ${theme.colors.ink}`, borderRadius: theme.radius.sm, padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          type="number"
          inputMode="decimal"
          autoFocus
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          placeholder={`сумма, ${currency}`}
          style={{
            flex: 1,
            padding: '10px 12px',
            fontSize: 18,
            border: `1px solid ${theme.colors.line}`,
            borderRadius: theme.radius.sm,
            background: theme.colors.card,
            fontFamily: theme.fontDisplay,
            color: theme.colors.ink,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <button
          onClick={() => { setOpen(false); setAmountStr(''); }}
          style={{
            padding: '0 12px',
            background: 'transparent',
            border: `1px solid ${theme.colors.line}`,
            borderRadius: theme.radius.sm,
            color: theme.colors.sub,
            cursor: 'pointer',
          }}
        >
          <Icon name="close" size={16} />
        </button>
      </div>
      <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: theme.colors.sub, marginBottom: 8 }}>
        в категорию ↓
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {frequent.map((c) => (
          <button
            key={c.name}
            onClick={() => add(c)}
            disabled={busy}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              background: theme.colors.card,
              border: `1px solid ${theme.colors.line}`,
              borderRadius: 999,
              fontFamily: theme.font,
              fontSize: 13,
              color: theme.colors.ink,
              cursor: 'pointer',
            }}
          >
            <Icon name={categoryIconName(c)} size={15} color={theme.colors.ink} />
            {c.name}
          </button>
        ))}
      </div>
      {canAdd && (
        <div style={{ fontSize: 12, color: theme.colors.sub, marginTop: 10 }}>
          тап по категории — запишет {fmtMoney(amount, currency)} сегодня
        </div>
      )}
    </div>
  );
}
